import React, { useState, useCallback, useEffect } from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { Button } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const ADMIN_STEPS = [
  {
    target: 'body',
    content: (
      <div>
        <h3 style={{ margin: '0 0 8px 0', fontSize: 18 }}>Welcome to ATUTEIN</h3>
        <p style={{ margin: 0, lineHeight: 1.5 }}>
          This quick tour will help you navigate the admin dashboard. Use the sidebar on the left to access all features.
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="sidebar"]',
    content: (
      <div>
        <h3 style={{ margin: '0 0 8px 0', fontSize: 18 }}>Navigation Menu</h3>
        <p style={{ margin: 0, lineHeight: 1.5 }}>
          <strong>Dashboard</strong> – Overview & metrics. <strong>Members</strong> – Manage members. <strong>Finance</strong> – Income, expenditure, invoices. <strong>HR</strong> – Positions, executives, elections, events. <strong>Assets</strong> – Track assets. <strong>Reports</strong> – Analytics. <strong>Communication</strong> – SMS, email, announcements.
        </p>
      </div>
    ),
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '[data-tour="profile"]',
    content: (
      <div>
        <h3 style={{ margin: '0 0 8px 0', fontSize: 18 }}>Your Profile</h3>
        <p style={{ margin: 0, lineHeight: 1.5 }}>
          Click here to update your profile, change your password, or upload a profile picture.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: 'body',
    content: (
      <div>
        <h3 style={{ margin: '0 0 8px 0', fontSize: 18 }}>You're All Set!</h3>
        <p style={{ margin: 0, lineHeight: 1.5 }}>
          Start with the Dashboard to see key metrics, or explore any section from the sidebar. You can restart this tour anytime using the help button.
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
];

const MEMBER_STEPS = [
  {
    target: 'body',
    content: (
      <div>
        <h3 style={{ margin: '0 0 8px 0', fontSize: 18 }}>Welcome to the Member Portal</h3>
        <p style={{ margin: 0, lineHeight: 1.5 }}>
          This tour will show you around. Use the menu to access your profile, events, announcements, elections, and more.
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="member-nav"]',
    content: (
      <div>
        <h3 style={{ margin: '0 0 8px 0', fontSize: 18 }}>Navigation</h3>
        <p style={{ margin: 0, lineHeight: 1.5 }}>
          <strong>Dashboard</strong> – Overview. <strong>Profile</strong> – Your details. <strong>Events</strong> – Upcoming events. <strong>Announcements</strong> – News. <strong>Elections</strong> – Vote. <strong>Executives</strong> – Leadership. <strong>My ID Card</strong> – Digital membership card.
        </p>
      </div>
    ),
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: 'body',
    content: (
      <div>
        <h3 style={{ margin: '0 0 8px 0', fontSize: 18 }}>You're Ready!</h3>
        <p style={{ margin: 0, lineHeight: 1.5 }}>
          Explore the portal. Check announcements, register for events, and vote in elections when they're open.
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
];

const TOUR_STORAGE_KEYS = {
  admin: 'dms_admin_tour_completed',
  member: 'dms_member_tour_completed',
};

export function AdminTour({ run, onComplete }) {
  const [runTour, setRunTour] = useState(run);

  const handleCallback = useCallback(
    (data) => {
      const { action, status, type } = data;
      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        if (action === ACTIONS.CLOSE || status === STATUS.SKIPPED || status === STATUS.FINISHED) {
          setRunTour(false);
          try {
            localStorage.setItem(TOUR_STORAGE_KEYS.admin, 'true');
          } catch (e) {}
          onComplete?.();
        }
      }
    },
    [onComplete]
  );

  return (
    <Joyride
      steps={ADMIN_STEPS}
      run={runTour}
      continuous
      showProgress
      showSkipButton
      callback={handleCallback}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip tour',
      }}
      styles={{
        options: {
          primaryColor: '#1976d2',
          zIndex: 10000,
          arrowColor: '#fff',
          backgroundColor: '#fff',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          textColor: '#333',
        },
        tooltip: {
          borderRadius: 8,
          padding: 20,
          fontSize: 14,
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        buttonNext: {
          backgroundColor: '#1976d2',
        },
        buttonBack: {
          color: '#666',
        },
      }}
      floaterProps={{
        disableAnimation: false,
      }}
    />
  );
}

export function MemberTour({ run, onComplete }) {
  const [runTour, setRunTour] = useState(false);

  useEffect(() => {
    if (run) setRunTour(true);
  }, [run]);

  const handleCallback = useCallback(
    (data) => {
      const { action, status, type } = data;
      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        if (action === ACTIONS.CLOSE || status === STATUS.SKIPPED || status === STATUS.FINISHED) {
          setRunTour(false);
          try {
            localStorage.setItem(TOUR_STORAGE_KEYS.member, 'true');
          } catch (e) {}
          onComplete?.();
        }
      }
    },
    [onComplete]
  );

  return (
    <Joyride
      steps={MEMBER_STEPS}
      run={runTour}
      continuous
      showProgress
      showSkipButton
      callback={handleCallback}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip tour',
      }}
      styles={{
        options: {
          primaryColor: '#1976d2',
          zIndex: 10000,
          arrowColor: '#fff',
          backgroundColor: '#fff',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          textColor: '#333',
        },
        tooltip: {
          borderRadius: 8,
          padding: 20,
          fontSize: 14,
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        buttonNext: {
          backgroundColor: '#1976d2',
        },
        buttonBack: {
          color: '#666',
        },
      }}
    />
  );
}

export function useAdminTour() {
  const [run, setRun] = useState(false);

  const shouldRunFirstTime = () => {
    try {
      return !localStorage.getItem(TOUR_STORAGE_KEYS.admin);
    } catch {
      return false;
    }
  };

  const startTour = () => {
    setRun(true);
  };

  const resetTour = () => {
    try {
      localStorage.removeItem(TOUR_STORAGE_KEYS.admin);
    } catch (e) {}
    setRun(true);
  };

  return { run, startTour, resetTour, shouldRunFirstTime };
}

export function useMemberTour() {
  const [run, setRun] = useState(false);

  const shouldRunFirstTime = useCallback(() => {
    try {
      return !localStorage.getItem(TOUR_STORAGE_KEYS.member);
    } catch {
      return false;
    }
  }, []);

  const startTour = useCallback(() => setRun(true), []);

  const resetTour = useCallback(() => {
    try {
      localStorage.removeItem(TOUR_STORAGE_KEYS.member);
    } catch (e) {}
    setRun(true);
  }, []);

  return { run, startTour, resetTour, shouldRunFirstTime };
}

export function TourHelpButton({ onClick, sx = {}, iconOnly }) {
  return (
    <Button
      variant="outlined"
      size="small"
      startIcon={!iconOnly && <HelpOutlineIcon />}
      onClick={onClick}
      aria-label="Start guided tour"
      sx={{
        minWidth: iconOnly ? 40 : 100,
        px: iconOnly ? 1 : 2,
        ...sx,
      }}
    >
      {iconOnly ? <HelpOutlineIcon /> : 'Start Tour'}
    </Button>
  );
}
